import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Text
} from '@chakra-ui/react'
import { PlusIcon, TrashIcon } from '@heroicons/react/16/solid'
import { DashboardApi20240730 } from '@sequencehq/api/dist/clients/dashboard/v20240730'
import {
  InfoPopover,
  InputSelectControlLegacyStyleBridge,
  ModalContextProvider,
  SelectField,
  SimpleAsyncModalUI,
  TextField,
  TextInput
} from '@sequencehq/core-components'
import { Country, State, TaxStatus } from '@sequencehq/core-models'
import {
  GreyGrey70,
  GreyGrey80,
  IndigoIndigo50,
  Lato14Bold,
  Lato14Regular,
  Lato16Bold
} from '@sequencehq/design-tokens'
import { useCreateCustomerForm } from 'components/Customers/components/drawers/CreateCustomer/hooks/useCreateCustomerForm.ts'
import { useState } from 'react'
import {
  CreateCustomerError,
  useCreateCustomer,
  PerformCreateCustomer
} from 'components/Customers/hooks/useCreateCustomer.ts'
import { CustomerFormErrorBanner } from 'Customer/components/CustomerFormErrorBanner'
import { CreateCustomer } from 'components/Customers/types'

interface Props {
  onClose: () => void
  onSuccess?: (
    customer: DashboardApi20240730.PostCustomer.CreateCustomerResponse
  ) => void
  createCustomer?: PerformCreateCustomer
  prefill?: Partial<CreateCustomer>
  additionalFieldProps?: (fieldName: string) => {
    trailingIcon?: React.ReactNode
  }
  drawerLeadingContent?: React.ReactNode
}
export const CreateCustomerForm = (props: Props) => {
  const [flowComplete, setFlowComplete] = useState(false)
  const { additionalFieldProps, prefill, drawerLeadingContent } = props

  const createCustomer = useCreateCustomer({
    performCreateCustomer: props.createCustomer
  })

  const { fieldsConfig, canCreate, isSaving, handleSubmit } =
    useCreateCustomerForm({ onSubmit: createCustomer, prefill })
  const [createError, setCreateError] = useState<CreateCustomerError>()

  const handleCreate = async () => {
    setCreateError(undefined)
    const result = await handleSubmit()

    if (result?.error) {
      setCreateError(result?.error)
      return false
    }

    if (result?.customer) {
      props.onSuccess?.(result.customer)
      setFlowComplete(true)
      return true
    }

    return false
  }

  const avalaraAddressError = createError?.type === 'INVALID_AVALARA_ADDRESS'

  return (
    <ModalContextProvider isOpen={!flowComplete}>
      <SimpleAsyncModalUI
        variant="drawer"
        title="Create customer"
        cancelButtonText="Cancel"
        submitButtonText="Create customer"
        submittingButtonText="Creating customer..."
        onClose={props.onClose}
        onCancel={props.onClose}
        onSubmit={handleCreate}
        submitDisabled={!canCreate || isSaving}
        closeOnSubmit={false}
        drawerLeadingContent={drawerLeadingContent}
      >
        <Flex gap="16px" direction="column">
          <CustomerFormErrorBanner operation="CREATE" error={createError} />
          <Text {...Lato16Bold}>Account information</Text>
          <Flex direction="column">
            <TextField
              label="Company name"
              value={fieldsConfig.legalName.value}
              onChange={fieldsConfig.legalName.onChange}
              validationErrors={fieldsConfig.legalName.validationErrors}
              isDisabled={fieldsConfig.legalName.disabled}
              autoFocus
              data-testid="createCustomerForm.companyName"
              {...additionalFieldProps?.('legalName')}
            />
          </Flex>

          <Flex direction="column">
            <Text {...Lato16Bold}>Billing contact</Text>
            <Flex mt="16px" gap={2}>
              <TextField
                placeholder="Full name"
                label="Full name"
                value={fieldsConfig.contactName.value}
                onChange={fieldsConfig.contactName.onChange}
                validationErrors={fieldsConfig.contactName.validationErrors}
                isDisabled={fieldsConfig.contactName.disabled}
                data-testid="createCustomerForm.primaryContactName"
                {...additionalFieldProps?.('contactName')}
              />
            </Flex>
            <TextField
              placeholder="Email"
              label="Email"
              value={fieldsConfig.email.value}
              onChange={fieldsConfig.email.onChange}
              validationErrors={fieldsConfig.email.validationErrors}
              isDisabled={fieldsConfig.email.disabled}
              data-testid="createCustomerForm.primaryContactEmail"
              {...additionalFieldProps?.('contactEmail')}
            />
          </Flex>
          <Text {...Lato16Bold}>Billing information</Text>

          <Flex direction="column">
            <Text {...Lato14Bold} color={GreyGrey80}>
              Billing details
            </Text>

            <Box mb="16px" mt="16px">
              <FormControl isInvalid={avalaraAddressError}>
                <InputSelectControlLegacyStyleBridge
                  triggerStyle={{
                    paddingLeft: 16,
                    userSelect: 'none'
                  }}
                  initialValue={fieldsConfig['address.country'].value}
                  onChange={newCountryValue =>
                    fieldsConfig['address.country'].onChange(
                      newCountryValue as Country
                    )
                  }
                  options={fieldsConfig['address.country'].options}
                  placeholder="Select country"
                  {...additionalFieldProps?.('address.country')}
                />
              </FormControl>
            </Box>

            {fieldsConfig['address.country'].showAddressFields && (
              <>
                <TextField
                  isInvalid={avalaraAddressError}
                  value={fieldsConfig['address.line1'].value}
                  onChange={fieldsConfig['address.line1'].onChange}
                  placeholder="Address line 1"
                  validationErrors={
                    fieldsConfig['address.line1'].validationErrors
                  }
                  data-testid="createCustomerForm.address.line1"
                  {...additionalFieldProps?.('address.line1')}
                />

                <TextField
                  isInvalid={avalaraAddressError}
                  value={fieldsConfig['address.line2'].value}
                  onChange={fieldsConfig['address.line2'].onChange}
                  placeholder="Address line 2 (optional)"
                  validationErrors={[]}
                  {...additionalFieldProps?.('address.line2')}
                />

                <TextField
                  isInvalid={avalaraAddressError}
                  value={fieldsConfig['address.town'].value}
                  onChange={fieldsConfig['address.town'].onChange}
                  placeholder="City/town"
                  validationErrors={
                    fieldsConfig['address.town'].validationErrors
                  }
                  data-testid="createCustomerForm.address.town"
                  {...additionalFieldProps?.('address.town')}
                />

                {fieldsConfig['address.state'].visible && (
                  <Box mb="16px">
                    <FormControl
                      isInvalid={
                        fieldsConfig['address.state'].validationErrors.length >
                          0 || avalaraAddressError
                      }
                    >
                      <InputSelectControlLegacyStyleBridge
                        triggerStyle={{
                          paddingLeft: 16,
                          userSelect: 'none'
                        }}
                        onChange={newStateValue =>
                          fieldsConfig['address.state'].onChange(
                            newStateValue as never
                          )
                        }
                        options={fieldsConfig['address.state'].options}
                        placeholder="Choose state..."
                        {...additionalFieldProps?.('address.state')}
                      />
                      {fieldsConfig['address.state'].validationErrors.length >
                        0 && (
                        <FormErrorMessage>
                          {
                            fieldsConfig['address.state'].validationErrors[0]
                              .message
                          }
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  </Box>
                )}

                <TextField
                  isInvalid={avalaraAddressError}
                  value={fieldsConfig['address.postcode'].value}
                  placeholder="Postcode"
                  onChange={fieldsConfig['address.postcode'].onChange}
                  validationErrors={
                    fieldsConfig['address.postcode'].validationErrors
                  }
                  data-testid="createCustomerForm.address.postcode"
                  {...additionalFieldProps?.('address.postcode')}
                />
              </>
            )}

            <SelectField
              label="Tax status"
              data-testid="createCustomerForm.taxStatus"
              value={fieldsConfig.taxStatus.value}
              onChange={newTaxStatus =>
                fieldsConfig.taxStatus.onChange(newTaxStatus as TaxStatus)
              }
              options={fieldsConfig.taxStatus.options}
              validationErrors={fieldsConfig.taxStatus.validationErrors}
              isDisabled={fieldsConfig.taxStatus.disabled}
              placeholder="Select tax status"
            />

            <Flex flex={1} alignItems="center" justifyContent="space-between">
              <Checkbox
                isChecked={fieldsConfig.addTaxRegistration.value}
                onChange={event => {
                  fieldsConfig.addTaxRegistration.onChange(event.target.checked)
                }}
                mb="16px"
                data-testid="createCustomerForm.showTaxRegistration"
              >
                <Text {...Lato14Regular} paddingRight="8px">
                  Add tax registration
                </Text>
              </Checkbox>
              <InfoPopover
                title="Tax registration"
                body="Add a tax registration ID for your customer. This is often used for cross-border transactions."
              />
            </Flex>
            {fieldsConfig.addTaxRegistration.showTaxRegistrationFields && (
              <Flex flexDirection="column">
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  flex={1}
                  flexDirection="row"
                >
                  <Box mr={4}>
                    <InputSelectControlLegacyStyleBridge
                      triggerStyle={{ paddingLeft: 16 }}
                      initialValue={fieldsConfig['taxCountry'].value}
                      onChange={newCountryValue =>
                        fieldsConfig['taxCountry'].onChange(
                          newCountryValue as Country
                        )
                      }
                      options={fieldsConfig['taxCountry'].options}
                      placeholder="Select country"
                      data-testid="createCustomerForm.taxCountry"
                    />
                  </Box>

                  <TextField
                    placeholder="Tax ID"
                    data-testid="createCustomerForm.taxId"
                    value={fieldsConfig.taxIdentifier.value}
                    onChange={fieldsConfig.taxIdentifier.onChange}
                    validationErrors={
                      fieldsConfig.taxIdentifier.validationErrors
                    }
                    styles={{
                      wrapper: {
                        mb: 0,
                        flex: 1
                      }
                    }}
                  />
                </Flex>

                {fieldsConfig['taxState'].visible && (
                  <>
                    <Box height="16px"></Box>
                    <Box mb="16px">
                      <FormControl
                        isInvalid={
                          fieldsConfig['taxState'].validationErrors.length > 0
                        }
                      >
                        <InputSelectControlLegacyStyleBridge
                          triggerStyle={{
                            paddingLeft: 16,
                            userSelect: 'none'
                          }}
                          onChange={newStateValue =>
                            fieldsConfig['taxState'].onChange(
                              newStateValue as State
                            )
                          }
                          options={fieldsConfig['taxState'].options}
                          placeholder="Choose state..."
                          data-testid="createCustomerForm.taxState"
                        />
                        {fieldsConfig['taxState'].validationErrors.length >
                          0 && (
                          <FormErrorMessage>
                            {
                              fieldsConfig['taxState'].validationErrors[0]
                                .message
                            }
                          </FormErrorMessage>
                        )}
                      </FormControl>
                    </Box>
                  </>
                )}
              </Flex>
            )}
          </Flex>

          <Text {...Lato16Bold}>Other information</Text>
          <Flex direction="column">
            <TextField
              label="Label (optional)"
              value={fieldsConfig.label.value}
              onChange={fieldsConfig.label.onChange}
              validationErrors={fieldsConfig.label.validationErrors}
            />

            <Box marginBottom="16px">
              <Text {...Lato14Bold} marginBottom="8px" color={GreyGrey70}>
                Parent customer (optional)
              </Text>

              <InputSelectControlLegacyStyleBridge
                triggerStyle={{ paddingLeft: 16 }}
                initialValue={fieldsConfig.parentId.value}
                onChange={newCountryValue =>
                  fieldsConfig.parentId.onChange(newCountryValue)
                }
                options={fieldsConfig.parentId.options}
                placeholder="Choose parent account"
                disabled={
                  fieldsConfig.parentId.options.find(
                    option => option.value === fieldsConfig.parentId.value
                  )?.disabled
                }
              />
            </Box>

            <Text {...Lato14Bold} marginBottom="8px" color={GreyGrey70}>
              Aliases (optional)
            </Text>
            <Flex gap="16px" direction="column" marginBottom="8px">
              {fieldsConfig.customerAliases.value.map((alias, index) => (
                <Flex align="center" gap="4px" key={index}>
                  <TextInput
                    data-testid={`createCustomerForm.customerAliases.${index}`}
                    value={alias}
                    onChange={updatedAlias => {
                      const aliasesBefore =
                        fieldsConfig.customerAliases.value.slice(0, index)
                      const aliasesAfter =
                        fieldsConfig.customerAliases.value.slice(index + 1) ??
                        []

                      fieldsConfig.customerAliases.onChange([
                        ...aliasesBefore,
                        updatedAlias,
                        ...aliasesAfter
                      ])
                    }}
                  />
                  <IconButton
                    aria-label="Remove alias"
                    minWidth="16px"
                    data-testid={`createCustomerForm.customerAliases.${index}.remove`}
                    isDisabled={!fieldsConfig.customerAliases.deleteAvailable}
                    height="16px"
                    width="16px"
                    icon={<TrashIcon height="16px" width="16px" />}
                    onClick={() => fieldsConfig.customerAliases.onRemove(index)}
                    variant="ghost2"
                  />
                </Flex>
              ))}
            </Flex>
            <Button
              color={IndigoIndigo50}
              data-testid="createCustomerForm.addAlias"
              padding="4px"
              height="auto"
              variant="ghost2"
              onClick={fieldsConfig.customerAliases.onAddNew}
              leftIcon={
                <PlusIcon color={IndigoIndigo50} height={16} width={16} />
              }
            >
              Add another customer alias
            </Button>
          </Flex>
        </Flex>
      </SimpleAsyncModalUI>
    </ModalContextProvider>
  )
}
